import type { InstallationErrorStatus } from './composables/useInstallationAlerts'
import type { InstallationAnalysisErrorStatus } from './composables/useInstallationAnalysis'
import type { CommissioningStatusPayload } from '~/services-v2/financial-bff/commissioning-status.gql'

/** Valores alinhados ao `steps` após a Fase 3 (POLLING inserido, ANALYSIS = 4). */
export const commissioningFlowSteps = {
  LABELS: 1,
  CREDENTIALS: 2,
  POLLING: 3,
  ANALYSIS: 4,
} as const

export type CommissioningFlowStep = typeof commissioningFlowSteps[keyof typeof commissioningFlowSteps]

type CommissioningStatusData = NonNullable<CommissioningStatusPayload['data']>
type InstallationStatus = NonNullable<CommissioningStatusData['installation_status']> | 'REJECTED_BY_CUSTOMER'
type FailureReason = NonNullable<CommissioningStatusData['monitorings'][number]['commissioning_failure_reason']>

export interface ResolvedCommissioningFlow {
  step: CommissioningFlowStep
  stepContext?: string
  analysisStage: number
  analysisError: InstallationAnalysisErrorStatus | null
  labelsAlert: InstallationErrorStatus | null
  credentialsAlert: InstallationErrorStatus | null
}

const CREDENTIAL_FAILURE_REASONS: FailureReason[] = [
  'CREDENTIALS_NOT_FOUND',
  'CREDENTIALS_MISMATCH',
  'END_CUSTOMER_CREDENTIAL',
]

export const FAILURE_REASON_TO_INSTALLATION_ERROR: Record<FailureReason, InstallationErrorStatus> = {
  CREDENTIALS_NOT_FOUND: 'CREDENTIAL_NOT_FOUND',
  CREDENTIALS_MISMATCH: 'CREDENTIAL_MISMATCH',
  END_CUSTOMER_CREDENTIAL: 'END_CUSTOMER_CREDENTIAL',
  SERIAL_NUMBER_NOT_FOUND: 'SERIAL_NUMBER_NOT_FOUND',
  INSTALLATION_NOT_FOUND: 'INSTALLATION_NOT_FOUND',
  INVERTERS_NOT_FOUND: 'INVERTERS_NOT_FOUND',
  PLANT_DATA_NOT_FOUND: 'INSTALLATION_NOT_FOUND',
  NO_ENERGY_PRODUCED: 'NO_ENERGY_PRODUCED',
  POWER_MISMATCH: 'POWER_MISMATCH',
  TECHNICAL_FAILURE: 'TECHNICAL_FAILURE',
}

function pickPrimaryMonitoring(monitorings: CommissioningStatusData['monitorings']) {
  return monitorings.find(m => m.status === 'COMMISSIONING_FAILED')
    ?? monitorings[0]
}

function resolveFailureReason(monitorings: CommissioningStatusData['monitorings']): FailureReason | null {
  const failedMonitoring = monitorings.find(m => m.status === 'COMMISSIONING_FAILED')
  if (failedMonitoring?.commissioning_failure_reason)
    return failedMonitoring.commissioning_failure_reason

  return pickPrimaryMonitoring(monitorings)?.commissioning_failure_reason ?? null
}

function isCommissioningFailureContext(installationStatus: InstallationStatus): boolean {
  return installationStatus === 'COMMISSIONING_FAILED'
}

function resolveAnalysisStage(installationStatus: InstallationStatus): number {
  switch (installationStatus) {
    case 'WAITING_PARTNER_SUBMISSION':
    case 'COMMISSIONING_IN_PROGRESS':
    case 'COMMISSIONING_FINISHED':
    case 'COMMISSIONING_FAILED':
      return 1
    case 'WAITING_CUSTOMER_CONFIRMATION':
    case 'REJECTED_BY_CUSTOMER':
      return 2
    case 'APPROVED':
      return 3
  }
}

function resolveAnalysisErrorFromInstallationStatus(
  installationStatus: InstallationStatus,
): InstallationAnalysisErrorStatus | null {
  switch (installationStatus) {
    case 'WAITING_PARTNER_SUBMISSION':
      return 'WAITING_PARTNER_CONFIRMATION'
    case 'WAITING_CUSTOMER_CONFIRMATION':
      return 'WAITING_CUSTOMER_CONFIRMATION'
    case 'REJECTED_BY_CUSTOMER':
      return 'REJECTED_BY_CUSTOMER'
    case 'APPROVED':
      return 'APPROVED'
    case 'COMMISSIONING_IN_PROGRESS':
    case 'COMMISSIONING_FINISHED':
    case 'COMMISSIONING_FAILED':
      return null
  }
}

function resolveInstallationErrorFromFailureReason(failureReason: FailureReason): InstallationErrorStatus {
  return FAILURE_REASON_TO_INSTALLATION_ERROR[failureReason]
}

function resolveMacroStep(
  installationStatus: InstallationStatus,
  failureReason: FailureReason | null,
  isFailureContext: boolean,
): { step: CommissioningFlowStep, stepContext?: string } {
  if (installationStatus === 'REJECTED_BY_CUSTOMER')
    return { step: commissioningFlowSteps.ANALYSIS }

  if (isFailureContext) {
    if (failureReason && CREDENTIAL_FAILURE_REASONS.includes(failureReason)) {
      const installationError = resolveInstallationErrorFromFailureReason(failureReason)
      const stepContext = installationError === 'CREDENTIAL_MISMATCH' ? 'CREDENTIAL_MISMATCH' : undefined
      return { step: commissioningFlowSteps.CREDENTIALS, stepContext }
    }

    return { step: commissioningFlowSteps.LABELS }
  }

  switch (installationStatus) {
    case 'WAITING_PARTNER_SUBMISSION':
      return { step: commissioningFlowSteps.LABELS }
    case 'COMMISSIONING_IN_PROGRESS':
    case 'COMMISSIONING_FINISHED':
      return { step: commissioningFlowSteps.POLLING }
    case 'WAITING_CUSTOMER_CONFIRMATION':
    case 'APPROVED':
      return { step: commissioningFlowSteps.ANALYSIS }
    case 'COMMISSIONING_FAILED':
      return { step: commissioningFlowSteps.LABELS }
  }
}

function resolveLabelsAlert(
  installationStatus: InstallationStatus,
  failureReason: FailureReason | null,
  isFailureContext: boolean,
): InstallationErrorStatus | null {
  if (installationStatus === 'REJECTED_BY_CUSTOMER')
    return null

  if (!isFailureContext)
    return null

  if (!failureReason)
    return 'TECHNICAL_FAILURE'

  if (CREDENTIAL_FAILURE_REASONS.includes(failureReason))
    return null

  return resolveInstallationErrorFromFailureReason(failureReason)
}

function resolveCredentialsAlert(
  failureReason: FailureReason | null,
  isFailureContext: boolean,
): InstallationErrorStatus | null {
  if (!isFailureContext || !failureReason)
    return null

  if (!CREDENTIAL_FAILURE_REASONS.includes(failureReason))
    return null

  if (failureReason === 'CREDENTIALS_NOT_FOUND')
    return null

  return resolveInstallationErrorFromFailureReason(failureReason)
}

export function resolveCommissioningFlow(
  payload: CommissioningStatusPayload | null | undefined,
): ResolvedCommissioningFlow | null {
  if (!payload?.success || !payload.data?.installation_status)
    return null

  const { installation_status: installationStatus, monitorings } = payload.data
  const failureReason = resolveFailureReason(monitorings)
  const isFailureContext = isCommissioningFailureContext(installationStatus)
  const analysisError = resolveAnalysisErrorFromInstallationStatus(installationStatus)
  const { step, stepContext } = resolveMacroStep(installationStatus, failureReason, isFailureContext)

  return {
    step,
    stepContext,
    analysisStage: resolveAnalysisStage(installationStatus),
    analysisError,
    labelsAlert: resolveLabelsAlert(installationStatus, failureReason, isFailureContext),
    credentialsAlert: resolveCredentialsAlert(failureReason, isFailureContext),
  }
}
